import { CustomToast } from "@components/common/Alert";
import { DSModal } from "@components/design-system/organisms";
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";

// Module-level state so code outside React (e.g. Apollo ErrorLink) can check
// whether a recording is in progress and defer destructive actions until it ends.
let preventing = false;
let pendingAction = null;

function syncPreventing(value) {
  preventing = value;
  if (!value && pendingAction) {
    const fn = pendingAction;
    pendingAction = null;
    fn();
  }
}

export function guardedKickOut(fn) {
  if (preventing) {
    pendingAction = fn;
    toast.warning(
      <CustomToast message="Your session has expired. Your recording will be saved automatically to your unsynced recordings when you stop recording. Log back in and click the 'Sync Recordings' button to process it." />,
      { autoClose: false, toastId: "session-expired-recording" },
    );
    return;
  }
  fn();
}

const PreventRecordingContext = createContext();

export const usePreventRecordingInterruptions = () =>
  useContext(PreventRecordingContext);

export const PreventRecordingProvider = ({ children }) => {
  const [shouldPreventInterruptions, setShouldPreventInterruptions] =
    useState(false);
  const [shouldShowAreYouSureModal, setShouldShowAreYouSureModal] =
    useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => {});

  const showAreYouSureModal = (action) => {
    setConfirmAction(() => action);
    setShouldShowAreYouSureModal(true);
  };

  const hideAreYouSureModal = () => {
    setShouldShowAreYouSureModal(false);
  };

  useEffect(() => {
    syncPreventing(shouldPreventInterruptions);
    return () => {
      // Only reset the flag — don't execute pendingAction here.
      // Pending actions should only fire when shouldPreventInterruptions
      // intentionally becomes false (recording stops), not on effect
      // cleanup from re-renders or StrictMode double-invocation.
      preventing = false;
    };
  }, [shouldPreventInterruptions]);

  const handleActionWithPrevention = (action) => {
    if (shouldPreventInterruptions) {
      showAreYouSureModal(action);
    } else {
      action();
    }
  };

  return (
    <PreventRecordingContext.Provider
      value={{
        shouldPreventInterruptions,
        setShouldPreventInterruptions,
        shouldShowAreYouSureModal,
        showAreYouSureModal,
        hideAreYouSureModal,
        confirmAction,
        handleActionWithPrevention,
      }}
    >
      {children}
    </PreventRecordingContext.Provider>
  );
};

export const AreYouSureModal = ({
  shouldShowAreYouSureModal,
  hideAreYouSureModal,
  confirmAction,
}) => {
  const { setShouldPreventInterruptions } =
    usePreventRecordingInterruptions();

  return (
    <DSModal
      isOpen={shouldShowAreYouSureModal}
      onClose={hideAreYouSureModal}
      title="Are You Sure?"
      dataTestId="are-you-sure-modal"
      actions={[
        {
          label: "Return To Recording",
          variant: "cancel",
          onClick: hideAreYouSureModal,
        },
        {
          label: "Continue",
          variant: "primary",
          onClick: () => {
            confirmAction();
            hideAreYouSureModal();
            setShouldPreventInterruptions(false);
          },
        },
      ]}
    >
      <h5>
        This could interrupt your recording and it will be lost. Are
        you sure you want to continue?
      </h5>
    </DSModal>
  );
};
